import { z } from "zod";
import { ERROR_CODES } from "@racler/shared";

export const CHANNELS = ["system", "filesystem", "terminal", "agent", "git", "process", "transfer", "editor", "lsp"] as const;
export type Channel = (typeof CHANNELS)[number];

export const messageIdSchema = z.string().min(1).max(128);
export const machineIdSchema = z.uuid();

/**
 * Who caused a request. Set ONLY by the server when it forwards a browser request to an agent (browsers
 * that send an actor are rejected). Lets the agent attribute operations in its local audit log.
 * `kind: "ai_agent"` is reserved for the future agent runtime (spec §11).
 */
export const actorSchema = z.strictObject({
  kind: z.enum(["user", "ai_agent"]),
  userId: z.uuid(),
  sessionId: z.uuid(),
});
export type Actor = z.infer<typeof actorSchema>;

/**
 * The single wire envelope used on both hops (browser↔server, server↔agent).
 * `machineId` is only meaningful on the browser↔server hop: target for requests, source for events.
 */
export const envelopeSchema = z.strictObject({
  id: messageIdSchema,
  channel: z.enum(CHANNELS),
  type: z.string().min(1).max(64),
  payload: z.unknown(),
  machineId: machineIdSchema.optional(),
  actor: actorSchema.optional(),
});

export interface RaclerMessage<T = unknown> {
  id: string;
  channel: Channel;
  type: string;
  payload: T;
  machineId?: string;
  actor?: Actor;
}

export const errorPayloadSchema = z.strictObject({
  code: z.enum(ERROR_CODES),
  message: z.string().max(2000),
});
export type ErrorPayload = z.infer<typeof errorPayloadSchema>;

export const ERROR_TYPE = "error";
export const RESULT_SUFFIX = ".result";

export function resultType(requestType: string): string {
  return `${requestType}${RESULT_SUFFIX}`;
}

export function isReplyType(type: string): boolean {
  return type === ERROR_TYPE || type.endsWith(RESULT_SUFFIX);
}

export class ProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProtocolError";
  }
}

/** Parse a raw frame into an envelope. Never trusts input; throws ProtocolError on anything unexpected. */
export function parseEnvelope(raw: string): RaclerMessage {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new ProtocolError("frame is not valid JSON");
  }
  const parsed = envelopeSchema.safeParse(data);
  if (!parsed.success) throw new ProtocolError("invalid envelope");
  return parsed.data;
}

export function encode(message: RaclerMessage): string {
  return JSON.stringify(message);
}

export function errorMessage(
  replyTo: Pick<RaclerMessage, "id" | "channel">,
  code: ErrorPayload["code"],
  message: string,
  machineId?: string,
): RaclerMessage<ErrorPayload> {
  const msg: RaclerMessage<ErrorPayload> = {
    id: replyTo.id,
    channel: replyTo.channel,
    type: ERROR_TYPE,
    payload: { code, message },
  };
  if (machineId) msg.machineId = machineId;
  return msg;
}
